/**
 * Shared Parameter Integration Hook
 *
 * Integrates the shared parameter store with the parameter workflow store.
 * This hook ensures that when shared parameters are loaded, they are available
 * as options throughout the application.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useSharedParameterStore } from '../stores/useSharedParameterStore';
import { useParameterWorkflowStore } from '../stores/useParameterWorkflowStore';
import type { ParameterOption } from '../stores/useParameterWorkflowStore';
import { useConfigurationStore } from '../stores/useConfigurationStore';
import {
  DEFAULT_PARAMETER_WORKFLOW_PARAMETERS,
  DEFAULT_PARAMETER_WORKFLOW_SELECTED_CATEGORIES,
  DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING,
} from '../config/parameterWorkflowDefaults';

/**
 * Hook to initialize shared parameters on app load
 * Should be called once at the app root level
 */
export function useInitializeSharedParameters() {
  const isParsed = useSharedParameterStore((state) => state.isParsed);
  const isLoading = useSharedParameterStore((state) => state.isLoading);
  const useDefaultFile = useSharedParameterStore((state) => state.useDefaultFile);
  const loadDefaultParameters = useSharedParameterStore((state) => state.loadDefaultParameters);
  const initRef = useRef(false);

  useEffect(() => {
    // Load default parameters on first mount if not already loaded
    if (!initRef.current && !isParsed && !isLoading && useDefaultFile) {
      initRef.current = true;
      loadDefaultParameters();
    }
  }, [isParsed, isLoading, useDefaultFile, loadDefaultParameters]);

  return { isParsed, isLoading };
}

/**
 * Hook to sync shared parameters with workflow store
 * This updates the parameter options available for selection
 */
export function useSyncSharedParametersWithWorkflow() {
  const sharedParams = useSharedParameterStore((state) => state.parsedParameters);
  const isParsed = useSharedParameterStore((state) => state.isParsed);
  const setAllParameters = useParameterWorkflowStore((state) => state.setAllParameters);

  // Use ref to track if sync has been done for current shared params
  const syncedParamsCountRef = useRef(0);

  const syncParameters = useCallback(() => {
    if (!isParsed || sharedParams.length === 0) return;

    // Prevent re-sync if already synced for this set of parameters
    if (syncedParamsCountRef.current === sharedParams.length) return;
    syncedParamsCountRef.current = sharedParams.length;

    // Get current workflow params directly from store to avoid dependency issues
    const currentWorkflowParams = useParameterWorkflowStore.getState().parameters;

    // Create a map of existing selections
    const existingSelections = new Map<string, boolean>();
    if (Array.isArray(currentWorkflowParams)) {
      currentWorkflowParams.forEach((p) => {
        if (p && p.name) {
          existingSelections.set(p.name, p.selected);
        }
      });
    }

    // Convert shared parameters to workflow parameter options
    // Preserve existing selections where possible
    const newParams: ParameterOption[] = sharedParams.map((sp) => ({
      name: sp.name,
      selected: existingSelections.has(sp.name)
        ? existingSelections.get(sp.name)!
        : isDefaultSelectedParameter(sp.name),
      description: sp.description || `${sp.dataType} parameter in ${sp.groupName || 'Default'} group`,
      dataType: sp.dataType,
    }));

    setAllParameters(newParams);
  }, [sharedParams, isParsed, setAllParameters]);

  return { syncParameters };
}

/**
 * Determines if a parameter should be selected by default
 */
function isDefaultSelectedParameter(paramName: string): boolean {
  // These are the core parameters typically needed for PCF export
  const defaultSelected = [
    '#PCF_OBJECT_TYPE',
    '#PCF_SKEY',
    'PiP_PPG_SPEC',
  ];
  return defaultSelected.includes(paramName);
}

/**
 * Hook to handle custom file upload and parsing
 */
export function useCustomSharedParameterFile() {
  const loadFileContent = useSharedParameterStore((state) => state.loadFileContent);
  const setFilePath = useSharedParameterStore((state) => state.setFilePath);
  const setUseDefaultFile = useSharedParameterStore((state) => state.setUseDefaultFile);
  const error = useSharedParameterStore((state) => state.error);
  const isParsed = useSharedParameterStore((state) => state.isParsed);
  const isLoading = useSharedParameterStore((state) => state.isLoading);

  const handleFileUpload = useCallback(
    (file: File) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = e.target?.result;
        if (typeof content === 'string') {
          setUseDefaultFile(false);
          setFilePath(file.name);
          loadFileContent(content);
        }
      };

      reader.onerror = () => {
        console.error('Error reading file:', reader.error);
      };

      // Read as text with UTF-16 support
      reader.readAsText(file, 'utf-16');
    },
    [loadFileContent, setFilePath, setUseDefaultFile]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
      // Reset input so same file can be selected again
      if (event.target) {
        event.target.value = '';
      }
    },
    [handleFileUpload]
  );

  const switchToDefaultFile = useCallback(() => {
    setUseDefaultFile(true);

    // Switching back to the shipped file discards every custom-file selection
    // so the wizard returns to the PipeEX defaults instead of keeping stale
    // persisted choices that may not exist in the default file.
    const workflowStore = useParameterWorkflowStore.getState();
    workflowStore.setAllParameters(
      DEFAULT_PARAMETER_WORKFLOW_PARAMETERS.map((param) => ({ ...param }))
    );
    workflowStore.setSelectedCategories([...DEFAULT_PARAMETER_WORKFLOW_SELECTED_CATEGORIES]);
    workflowStore.setSystemGroupingPreference(DEFAULT_PARAMETER_WORKFLOW_SYSTEM_GROUPING);

    useConfigurationStore.getState().resetWizardSelectionsToDefaults();
  }, [setUseDefaultFile]);

  return {
    handleFileUpload,
    handleFileSelect,
    switchToDefaultFile,
    error,
    isParsed,
    isLoading,
  };
}

/**
 * Hook to get parameters ready for Revit creation
 */
export function useParametersForRevitCreation() {
  const sharedParams = useSharedParameterStore((state) => state.parsedParameters);
  const workflowParams = useParameterWorkflowStore((state) => state.parameters);
  const selectedCategories = useParameterWorkflowStore((state) => state.selectedCategories);

  const getSelectedParametersForCreation = useCallback(() => {
    // Get names of selected parameters
    const selectedNames = new Set(
      workflowParams.filter((p) => p && p.selected).map((p) => p.name)
    );

    // Get full parameter data for selected ones
    const parametersToCreate = sharedParams
      .filter((sp) => selectedNames.has(sp.name))
      .map((sp) => ({
        name: sp.name,
        guid: sp.guid,
        dataType: sp.dataType,
        groupId: sp.groupId,
        description: sp.description,
      }));

    return {
      parameters: parametersToCreate,
      categories: selectedCategories,
      isType: true, // Default to type parameters
    };
  }, [sharedParams, workflowParams, selectedCategories]);

  return { getSelectedParametersForCreation };
}
